import React from 'react';
import { Modal, Button, Accordion, Badge, Alert } from 'react-bootstrap';
import moment from 'moment';

/**
 * Sanitiza una serie para embeberla en texto libre (T10 threat model).
 * Defensa en profundidad — el backend también sanitiza.
 */
const sanitizarSerie = (s) => String(s || '')
    .replace(/[\r\n\t]/g, ' ')
    .replace(/[<>&"']/g, '')
    .slice(0, 60);

/**
 * ModalConfirmarDependientes - confirma inclusión de equipos hijos al agregar
 * un equipo padre a una OT. Spec: openspec/changes/items-adicionales-equipo-dependiente.
 *
 * Props:
 *   show                  boolean
 *   grupos                Array<{ padre, hijos, seleccionadoPadre, hijosNoSeleccionados }>
 *   onIncluirTodos        () => void          — agrega padres + hijos a la OT.
 *   onSoloSeleccionados   (warningString) => void — agrega solo lo seleccionado,
 *                         devuelve el warning para append en observación de OT.
 *   onCancelar            () => void          — cierra sin agregar nada.
 */
const ModalConfirmarDependientes = ({
    show,
    grupos = [],
    onIncluirTodos,
    onSoloSeleccionados,
    onCancelar,
}) => {

    const totalHijosOmitidos = grupos.reduce((n, g) => n + (g.hijosNoSeleccionados?.length || 0), 0);
    const totalPadresOmitidos = grupos.filter(g => !g.seleccionadoPadre).length;

    const handleSoloSeleccionados = () => {
        const seriesOmitidas = grupos.flatMap(g => (g.hijosNoSeleccionados || []).map(h => sanitizarSerie(h.serie)));
        // Formato del warning acordado en design.md §3.3 / spec §Requirement modal.
        const fecha = moment().format('YYYY-MM-DD HH:mm');
        const warningString = `[WARN ${fecha}] Equipos con dependientes no incluidos: ${seriesOmitidas.join(', ')}`;
        onSoloSeleccionados(warningString);
    };

    return (
        <Modal show={show} onHide={onCancelar} size="lg" backdrop="static" keyboard={false}>
            <Modal.Header closeButton={false}>
                <Modal.Title>
                    Confirmar equipos dependientes
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <Alert variant="warning" className="mb-3">
                    Los equipos que agregaste tienen <strong>{totalHijosOmitidos} equipo(s) dependiente(s)</strong>
                    {totalPadresOmitidos > 0 && ` y ${totalPadresOmitidos} equipo(s) padre(s)`}
                    {' '}que no están seleccionados. Se recomienda incluirlos para preservar la trazabilidad ISO 17025.
                </Alert>

                <Accordion defaultActiveKey="0" alwaysOpen>
                    {grupos.map((grupo, idx) => (
                        <Accordion.Item eventKey={String(idx)} key={grupo.padre?._id || idx}>
                            <Accordion.Header>
                                <span className="me-2">
                                    <Badge bg={grupo.seleccionadoPadre ? 'success' : 'danger'}>
                                        {grupo.seleccionadoPadre ? 'Padre incluido' : 'Padre NO incluido'}
                                    </Badge>
                                </span>
                                <strong>{grupo.padre?.item?.nombre || 'Item'}</strong>
                                <span className="text-muted ms-2">
                                    · Serie: {grupo.padre?.serie}
                                </span>
                            </Accordion.Header>
                            <Accordion.Body>
                                <div className="mb-2">
                                    <strong>Padre:</strong>{' '}
                                    {grupo.padre?.item?.nombre} — {grupo.padre?.marca} {grupo.padre?.modelo} — Serie {grupo.padre?.serie}
                                </div>
                                <div>
                                    <strong>Dependientes:</strong>
                                    <ul className="mb-0">
                                        {(grupo.hijos || []).map(hijo => {
                                            const noSeleccionado = (grupo.hijosNoSeleccionados || []).some(h => String(h._id) === String(hijo._id));
                                            return (
                                                <li key={hijo._id}>
                                                    {hijo.item?.nombre || 'Item'} — Serie {hijo.serie}
                                                    {noSeleccionado
                                                        ? <Badge bg="warning" text="dark" className="ms-2">NO incluido</Badge>
                                                        : <Badge bg="success" className="ms-2">Ya incluido</Badge>}
                                                </li>
                                            );
                                        })}
                                    </ul>
                                </div>
                            </Accordion.Body>
                        </Accordion.Item>
                    ))}
                </Accordion>
            </Modal.Body>
            <Modal.Footer>
                <Button variant="outline-secondary" onClick={onCancelar}>
                    Cancelar
                </Button>
                <Button variant="outline-warning" onClick={handleSoloSeleccionados}>
                    Solo estos equipos
                </Button>
                <Button variant="primary" onClick={onIncluirTodos} autoFocus>
                    Incluir todos (recomendado)
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default ModalConfirmarDependientes;
